import axios from "axios"
import { getToken, forceRefresh, logout } from "@/auth/keycloak"

// Serialize query params the way the Tools API expects: URLSearchParams encoding
// with array values collapsed to a single comma-joined param (e.g. stationKeys=a,b)
// rather than axios's default stationKeys[]=a&stationKeys[]=b. null/undefined are
// skipped (matching axios's own default), so absent params don't leak as "undefined".
const serializeParams = (params) => {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value)
    }
  })

  return searchParams.toString()
}

const toolsClient = axios.create({
  baseURL: process.env.VUE_APP_TOOLS_SERVICE_URL + "/",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
  },
  paramsSerializer: serializeParams,
})

toolsClient.interceptors.request.use(
  (config) => {
    const token = getToken()

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    console.log("[TOOLS] Request error", error.message, error.response)
    return Promise.reject(error)
  }
)

toolsClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // On 401, force a token refresh and retry the request once. If the refresh
    // itself fails, the session is unrecoverable, so log out.
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true

      try {
        await forceRefresh()
      } catch (refreshError) {
        console.error("[TOOLS] Token refresh failed; logging out", refreshError)
        await logout()
        return Promise.reject(error)
      }

      original.headers["Authorization"] = `Bearer ${getToken()}`

      return toolsClient(original)
    }

    if (error.response) {
      console.error("Server responded with an error:", error.response)
    } else if (error.request) {
      console.error("No response received:", error.request)
    } else {
      console.error("Request setup error:", error.message)
    }

    return Promise.reject(error)
  }
)

export default toolsClient
